import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { productsAPI } from '../../api';
import { AUTOCOMPLETE_MIN_CHARS, AUTOCOMPLETE_DEBOUNCE_MS } from '../../constants';

const ProductAutocomplete = ({
  field, // 'part_number' or 'description'
  value,
  onChange,
  onSelect,
  label,
  placeholder,
  disabled = false,
  error = false,
  helperText = '',
  size = 'small',
  ...props
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Sync input value with prop value
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value, inputValue]);

  const fetchOptions = async (searchTerm) => {
    if (searchTerm.length < AUTOCOMPLETE_MIN_CHARS) {
      setOptions([]);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setLoading(true);

    try {
      const params = {
        [field]: searchTerm
      };

      const response = await productsAPI.autofill(params);
      setOptions(response.data || []);
    } catch (error) {
      if (error.name === 'CanceledError') {
        // Request was cancelled, ignore
        return;
      }
      console.error('Autocomplete error:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event, newInputValue, reason) => {
    setInputValue(newInputValue);

    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Call onChange immediately for parent to update state
    if (onChange) {
      onChange(newInputValue);
    }

    // Debounce API call
    if (reason === 'input') {
      debounceTimerRef.current = setTimeout(() => {
        fetchOptions(newInputValue);
      }, AUTOCOMPLETE_DEBOUNCE_MS);
    } else if (reason === 'clear') {
      setOptions([]);
    }
  };

  const handleChange = (event, newValue) => {
    if (newValue && typeof newValue === 'object') {
      // User selected an option from dropdown
      if (onSelect) {
        onSelect(newValue);
      }
    }
  };

  const getOptionLabel = (option) => {
    if (typeof option === 'string') {
      return option;
    }

    if (field === 'part_number') {
      return option.part_number || '';
    } else if (field === 'description') {
      return option.description || '';
    }

    return '';
  };

  const renderOption = (props, option) => {
    return (
      <li {...props} key={option.part_number + option.description}>
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {option.part_number}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#666' }}>
            {option.description}
          </div>
          {option.last_value && (
            <div style={{ fontSize: '0.75rem', color: '#999' }}>
              Last price: €{parseFloat(option.last_value).toFixed(2)}
            </div>
          )}
        </div>
      </li>
    );
  };

  return (
    <Autocomplete
      freeSolo
      options={options}
      loading={loading}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      disabled={disabled}
      size={size}
      filterOptions={(x) => x} // Disable built-in filtering, use API results
      {...props}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ProductAutocomplete;
